import { ChevronDown } from "lucide-react";
import * as React from "react";

import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import type { Condition, Operator, Stat } from "@/lib/api";
import {
	buildEntityOptions,
	type EntityOption,
	entityName,
	type Players,
	type Teams,
} from "@/lib/entities";
import {
	COMPARISON_LABEL,
	hasValue,
	statLabel,
	windowPhrase,
} from "@/lib/rule-display";
import { cn } from "@/lib/utils";

const STAT_OPTIONS: Array<{ value: Stat; label: string }> = [
	{ value: "hr", label: "Home runs" },
	{ value: "rbi", label: "RBI" },
	{ value: "avg", label: "Batting average" },
	{ value: "obp", label: "On-base percentage" },
	{ value: "slg", label: "Slugging percentage" },
];

const COMPARISON_OPTIONS: Array<{ value: Operator; label: string }> = (
	["gte", "gt", "eq", "lte", "lt", "ne"] as Operator[]
).map((value) => ({ value, label: COMPARISON_LABEL[value] }));

const SIZE_CLASSES = {
	sm: "text-[0.9375rem]",
	md: "text-base",
	lg: "text-lg leading-relaxed",
} as const;

function EditorLabel({ children }: { children: React.ReactNode }) {
	return <div className="type-eyebrow mb-2">{children}</div>;
}

function ChoiceList<T extends string>({
	options,
	value,
	onChange,
	columns = 2,
	testIdPrefix,
}: {
	options: Array<{ value: T; label: string }>;
	value: T | null;
	onChange: (value: T) => void;
	columns?: 1 | 2;
	testIdPrefix?: string;
}) {
	return (
		<div className={cn("grid gap-1", columns === 2 && "grid-cols-2")}>
			{options.map((option) => (
				<button
					className={cn(
						"rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent",
						value === option.value
							? "bg-accent font-medium text-foreground"
							: "text-muted-foreground",
					)}
					data-testid={
						testIdPrefix ? `${testIdPrefix}-${option.value}` : undefined
					}
					key={option.value}
					onClick={() => onChange(option.value)}
					type="button"
				>
					{option.label}
				</button>
			))}
		</div>
	);
}

export function EntityPicker({
	options,
	value,
	onSelect,
}: {
	options: EntityOption[];
	value: string | null;
	onSelect: (option: EntityOption) => void;
}) {
	const [query, setQuery] = React.useState("");
	const normalized = query.trim().toLowerCase();
	const matches = normalized
		? options.filter((option) => option.search.includes(normalized))
		: options;
	const groups: Array<EntityOption["group"]> = ["Teams", "Players"];

	return (
		<div className="flex flex-col gap-2">
			<Input
				autoFocus
				className="h-8"
				data-testid="entity-search"
				onChange={(event) => setQuery(event.target.value)}
				placeholder="Search players and teams"
				value={query}
			/>
			<div className="max-h-64 overflow-y-auto">
				{groups.map((group) => {
					const items = matches
						.filter((option) => option.group === group)
						.slice(0, normalized ? 40 : 15);

					if (items.length === 0) {
						return null;
					}

					return (
						<div className="mb-2 last:mb-0" key={group}>
							<div className="type-eyebrow px-2 py-1.5">{group}</div>
							{items.map((option) => (
								<button
									className={cn(
										"flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent",
										option.value === value && "bg-accent",
									)}
									data-entity-id={option.entityID}
									data-entity-type={option.entityType}
									data-testid="entity-option"
									key={option.value}
									onClick={() => onSelect(option)}
									type="button"
								>
									<span className="truncate">{option.label}</span>
									<span className="type-data text-muted-foreground">
										{option.meta}
									</span>
								</button>
							))}
						</div>
					);
				})}
				{matches.length === 0 && (
					<p className="px-2 py-6 text-center text-sm text-muted-foreground">
						No players or teams found.
					</p>
				)}
			</div>
		</div>
	);
}

function FieldShell({
	label,
	value,
	children,
	placeholder,
	className,
	testId,
}: {
	label: string;
	value: React.ReactNode;
	children: (close: () => void) => React.ReactNode;
	placeholder?: boolean;
	className?: string;
	testId?: string;
}) {
	const [open, setOpen] = React.useState(false);

	return (
		<div
			className={cn("flex flex-col gap-1.5", className)}
			data-testid={testId}
		>
			<span className="text-xs text-muted-foreground">{label}</span>
			<Popover onOpenChange={setOpen} open={open}>
				<PopoverTrigger asChild>
					<button
						className={cn(
							"flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-card px-3 text-left text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
							placeholder && "text-muted-foreground",
						)}
						data-testid={testId ? `${testId}-trigger` : undefined}
						type="button"
					>
						<span className="truncate">{value}</span>
						<ChevronDown className="size-3.5 shrink-0 opacity-50" />
					</button>
				</PopoverTrigger>
				<PopoverContent
					align="start"
					className="w-[min(20rem,calc(100vw-2rem))] p-3"
				>
					{children(() => setOpen(false))}
				</PopoverContent>
			</Popover>
		</div>
	);
}

export function SubjectField({
	options,
	value,
	onChange,
	label = "Player or team",
	testId,
}: {
	options: EntityOption[];
	value: string | null;
	onChange: (option: EntityOption) => void;
	label?: string;
	testId?: string;
}) {
	const selected = options.find((option) => option.value === value);
	const [open, setOpen] = React.useState(false);

	return (
		<div className="flex flex-col gap-1.5" data-testid={testId}>
			<span className="text-xs text-muted-foreground">{label}</span>
			<Popover onOpenChange={setOpen} open={open}>
				<PopoverTrigger asChild>
					<button
						className={cn(
							"flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-card px-3 text-left text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
							!selected && "text-muted-foreground",
						)}
						data-testid={testId ? `${testId}-trigger` : undefined}
						type="button"
					>
						<span className="truncate">
							{selected?.label ?? "Choose a player or team"}
						</span>
						<ChevronDown className="size-3.5 shrink-0 opacity-50" />
					</button>
				</PopoverTrigger>
				<PopoverContent
					align="start"
					className="w-[min(20rem,calc(100vw-2rem))] p-3"
				>
					<EntityPicker
						onSelect={(option) => {
							onChange(option);
							setOpen(false);
						}}
						options={options}
						value={value}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
}

export function StatField({
	value,
	onChange,
	testId,
}: {
	value: Stat | null;
	onChange: (value: Stat) => void;
	testId?: string;
}) {
	const selected = STAT_OPTIONS.find((option) => option.value === value);

	return (
		<FieldShell
			label="Stat"
			placeholder={!selected}
			value={selected?.label ?? "Choose a stat"}
			testId={testId}
		>
			{(close) => (
				<ChoiceList
					onChange={(next) => {
						onChange(next);
						close();
					}}
					options={STAT_OPTIONS}
					testIdPrefix="stat-option"
					value={value}
				/>
			)}
		</FieldShell>
	);
}

export function ComparisonField({
	value,
	onChange,
	testId,
}: {
	value: Operator | null;
	onChange: (value: Operator) => void;
	testId?: string;
}) {
	const selected = COMPARISON_OPTIONS.find((option) => option.value === value);

	return (
		<FieldShell
			label="Comparison"
			placeholder={!selected}
			value={selected?.label ?? "Choose a comparison"}
			testId={testId}
		>
			{(close) => (
				<ChoiceList
					onChange={(next) => {
						onChange(next);
						close();
					}}
					options={COMPARISON_OPTIONS}
					testIdPrefix="comparison-option"
					value={value}
				/>
			)}
		</FieldShell>
	);
}

export function WindowField({
	value,
	over,
	onChange,
	onOverChange,
	testId,
}: {
	value: Condition["window"];
	over: number | null;
	onChange: (value: "game" | "inning") => void;
	onOverChange: (value: number) => void;
	testId?: string;
}) {
	const current = value ?? "game";

	return (
		<FieldShell
			label="Counted over"
			value={windowPhrase(value, over)}
			testId={testId}
		>
			{(close) => (
				<div className="flex flex-col gap-3">
					<ChoiceList
						onChange={(next) => {
							onChange(next);

							if (next === "game") {
								close();
							}
						}}
						options={[
							{ value: "game", label: "A game" },
							{ value: "inning", label: "Consecutive innings" },
						]}
						testIdPrefix="window-option"
						value={current}
					/>
					{current === "inning" && (
						<div className="flex items-center gap-2 border-t pt-3">
							<span className="text-sm text-muted-foreground">Innings</span>
							<Input
								className="h-8 w-20"
								data-testid="window-innings"
								min={1}
								onChange={(event) =>
									onOverChange(Number(event.target.value) || 1)
								}
								type="number"
								value={over ?? 1}
							/>
						</div>
					)}
				</div>
			)}
		</FieldShell>
	);
}

function Token({
	children,
	editable,
	editor,
	missing,
	className,
}: {
	children: React.ReactNode;
	editable?: boolean;
	editor?: (close: () => void) => React.ReactNode;
	missing?: boolean;
	className?: string;
}) {
	const [open, setOpen] = React.useState(false);
	const classes = cn(
		"rounded-[0.3em] px-[0.28em] font-semibold",
		missing
			? "border border-dashed border-token-underline/70 bg-transparent text-muted-foreground"
			: "bg-token text-token-foreground",
		editable &&
			"decoration-token-underline/70 underline-offset-4 hover:decoration-token-underline focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none",
		editable && "underline decoration-dotted",
		open && editable && "ring-2 ring-ring/40",
		className,
	);

	if (!editable || !editor) {
		return <span className={classes}>{children}</span>;
	}

	return (
		<Popover onOpenChange={setOpen} open={open}>
			<PopoverTrigger asChild>
				<button className={classes} type="button">
					{children}
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				className="w-[min(20rem,calc(100vw-2rem))] p-3"
			>
				{editor(() => setOpen(false))}
			</PopoverContent>
		</Popover>
	);
}

function subjectLabel(leaf: Condition, players: Players, teams: Teams): string {
	const name = entityName(leaf.entityType, leaf.entityID, players, teams);

	if (!name) {
		return "someone";
	}

	return leaf.entityType === "team" ? `the ${name}` : name;
}

function statWord(stat: Stat | null, value: unknown): string {
	if (stat === "hr" && Number(value) === 1) {
		return "home run";
	}

	return statLabel(stat);
}

function ConditionText({
	condition,
	players,
	teams,
	options,
	editable,
	onChange,
}: {
	condition: Condition;
	players: Players;
	teams: Teams;
	options: EntityOption[];
	editable: boolean;
	onChange?: () => void;
}) {
	const commit = () => onChange?.();
	const hasSubject = Boolean(condition.entityID && condition.entityType);

	return (
		<>
			<Token
				editable={editable}
				editor={(close) => (
					<>
						<EditorLabel>Player or team</EditorLabel>
						<EntityPicker
							onSelect={(option) => {
								condition.entity = option.value;
								close();
								commit();
							}}
							options={options}
							value={condition.entity}
						/>
					</>
				)}
				missing={!hasSubject}
			>
				{subjectLabel(condition, players, teams)}
			</Token>{" "}
			<span className="font-semibold">
				{condition.entityType === "team" ? "have" : "has"}
			</span>{" "}
			<Token
				editable={editable}
				editor={() => (
					<>
						<EditorLabel>Threshold</EditorLabel>
						<Input
							autoFocus
							className="h-8"
							onChange={(event) => {
								const next = event.target.value;
								condition.value = next === "" ? undefined : Number(next);
								commit();
							}}
							step="any"
							type="number"
							value={condition.value ?? ""}
						/>
					</>
				)}
				missing={!hasValue(condition)}
			>
				{hasValue(condition)
					? `${COMPARISON_LABEL[condition.operator ?? "gte"]} ${condition.value}`
					: `${COMPARISON_LABEL[condition.operator ?? "gte"]} a number`}
			</Token>{" "}
			<Token
				editable={editable}
				editor={(close) => (
					<>
						<EditorLabel>Stat</EditorLabel>
						<ChoiceList
							onChange={(next) => {
								condition.stat = next;
								close();
								commit();
							}}
							options={STAT_OPTIONS}
							value={condition.stat}
						/>
					</>
				)}
				missing={!condition.stat}
			>
				{statWord(condition.stat, condition.value)}
			</Token>{" "}
			<Token
				editable={editable}
				editor={(close) => (
					<>
						<EditorLabel>Counted over</EditorLabel>
						<ChoiceList
							onChange={(next) => {
								condition.window = next;
								condition.over = 1;
								close();
								commit();
							}}
							options={[
								{ value: "game", label: "A game" },
								{ value: "inning", label: "Consecutive innings" },
							]}
							value={condition.window ?? "game"}
						/>
						{condition.window === "inning" && (
							<div className="mt-3 flex items-center gap-2 border-t pt-3">
								<span className="text-sm text-muted-foreground">Innings</span>
								<Input
									className="h-8 w-20"
									min={1}
									onChange={(event) => {
										condition.over = Number(event.target.value) || 1;
										commit();
									}}
									type="number"
									value={condition.over ?? 1}
								/>
							</div>
						)}
					</>
				)}
			>
				{windowPhrase(condition.window, condition.over)}
			</Token>
		</>
	);
}

/**
 * Renders the API's condition tree as one readable sentence. Every highlighted
 * word is a control when `editable` is set.
 */
export function RuleSentence({
	condition,
	players,
	teams,
	options,
	editable = false,
	onChange,
	size = "md",
	className,
	testId,
}: {
	condition: Condition;
	players: Players;
	teams: Teams;
	options?: EntityOption[];
	editable?: boolean;
	onChange?: () => void;
	size?: keyof typeof SIZE_CLASSES;
	className?: string;
	testId?: string;
}) {
	const entityOptions = options ?? buildEntityOptions(players, teams);
	const leaves =
		condition.ruleType === "leaf"
			? [condition]
			: condition.rules.flatMap(function flatten(leaf): Condition[] {
					return leaf.ruleType === "leaf"
						? [leaf]
						: leaf.rules.flatMap(flatten);
				});
	const join = condition.ruleType === "or" ? "or" : "and";

	return (
		<p className={cn(SIZE_CLASSES[size], className)} data-testid={testId}>
			<span className="text-muted-foreground">When </span>
			{leaves.map((leaf, index) => (
				<React.Fragment key={leaf.id}>
					{index > 0 && <span className="text-muted-foreground"> {join} </span>}
					<ConditionText
						condition={leaf}
						editable={editable}
						onChange={onChange}
						options={entityOptions}
						players={players}
						teams={teams}
					/>
				</React.Fragment>
			))}
			<span className="text-muted-foreground">, just notify me.</span>
		</p>
	);
}
